import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { huHU } from '@mui/x-data-grid/locales';
import { isEqual } from 'lodash';
import {
  DataGridPremium,
  useGridApiRef,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarColumnsButton,
  GridLogicOperator,
  gridColumnFieldsSelector,
  GridToolbarExport,
} from '@mui/x-data-grid-premium';
import { useTheme } from '@mui/material/styles';

import { datagridLangHunCustomized } from '../../../config/datagridLangHunCustomized';
import useSaveView from '../../../hooks/useSaveView';
import { getDataGridStyles } from '../Datagrid/dataGridStyles';
import { DataGridIdentifiers } from '../Datagrid/DataGridIdentifiers';
import useUsers from '../../../context/UsersContext';

const DataGridPremiumUI = ({
  datagridIdentifier,
  dataLoader,
  paginationAndfilterMode = 'client',
  datagridInitialState,
  refetch = { refetchTable: false, setRefetchTable: () => {} },
  toolbar,
  gridExport,
  filterFromInitialState,
  customHeaderComponent,
  disableSave = false,
  ...props
}) => {
  const theme = useTheme();
  const arf = useGridApiRef();
  const apiRef = props.apiRef ?? arf;
  const { refetchTable, setRefetchTable } = refetch;
  const parentNode = document.body.parentNode;
  const height = parentNode.clientHeight * 0.85;

  const { getUser, user } = useUsers();
  const [isLoading, setisLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [queryOptions, setQueryOptions] = useState({
    pagination: {
      paginationModel: {
        pageSize: 25,
        page: 0,
      },
    },
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [],
    },
  });

  const [initialState, setInitialState] = useState({});
  const pendingRestoreStateRef = useRef(null);
  const isApplyingPendingRestoreRef = useRef(false);
  const saveViewToDb = useSaveView(user);

  const saveView = (viewType, viewData) => {
    if (disableSave) return;
    if (Object.keys(user).length > 0) {
      if (filterFromInitialState) {
        viewData = {
          [datagridIdentifier]: {
            ...viewData[datagridIdentifier],
            filter: { filterModel: { items: [], logicOperator: 'and' } },
            pagination: {
              paginationModel: {
                pageSize: 25,
                page: 0,
              },
            },
          },
        };
      }

      saveViewToDb(viewType, viewData);
    }
  };

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
  }, []);

  const useColumnsState = (apiRef, columns) => {
    const [widths, setWidths] = useState({});
    const [orderedFields, setOrderedFields] = useState(columns.map((column) => column.field));

    const onColumnWidthChange = React.useCallback(
      ({ colDef, width }) => {
        if (disableSave) {
          setWidths((prev) => ({ ...prev, [colDef.field]: width }));
          return;
        }
        setWidths((prev) => ({ ...prev, [colDef.field]: width }));
        const currentState = apiRef.current.exportState();
        saveView('dataGrid', { [datagridIdentifier]: currentState });
      },
      [setWidths, disableSave]
    );

    const onColumnOrderChange = React.useCallback(() => {
      if (disableSave) {
        setOrderedFields(gridColumnFieldsSelector(apiRef));
        return;
      }
      setOrderedFields(gridColumnFieldsSelector(apiRef));
      const currentState = apiRef.current.exportState();
      saveView('dataGrid', { [datagridIdentifier]: currentState });
    }, [apiRef, setOrderedFields, disableSave]);

    // Sync columns when props.columns changes (add new, remove missing, keep widths for existing)
    useEffect(() => {
      const currentFields = columns.map((c) => c.field);
      setOrderedFields((prev) => {
        const updatedFields = [
          ...prev.filter((f) => currentFields.includes(f)),
          ...currentFields.filter((f) => !prev.includes(f)),
        ];
        return isEqual(prev, updatedFields) ? prev : updatedFields;
      });
      setWidths((prev) => {
        const next = {};
        currentFields.forEach((f) => {
          if (prev[f] !== undefined) next[f] = prev[f];
        });
        return next;
      });
    }, [columns]);

    const computedColumns = React.useMemo(
      () =>
        orderedFields.reduce((acc, field) => {
          const column = columns.find((col) => col.field === field);
          if (!column) return acc;
          if (widths[field]) {
            acc.push({ ...column, width: widths[field] });
            return acc;
          }
          acc.push(column);
          return acc;
        }, []),
      [columns, widths, orderedFields]
    );

    return { columns: computedColumns, onColumnWidthChange, onColumnOrderChange, setOrderedFields };
  };

  const columnsState = useColumnsState(apiRef, props.columns);

  const handleEvent = (_params, _event, _details) => {
    if (disableSave) return;
    if (Object.keys(_params).length > 0) {
      const currentState = apiRef.current.exportState();
      setInitialState(currentState);
      saveView('dataGrid', { [datagridIdentifier]: currentState });
    }
  };

  const abortControllerRef = useRef();

  const loader = async (tableState) => {
    setisLoading(true);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    try {
      if (tableState.pagination?.paginationModel?.pageSize === -1 && paginationAndfilterMode === 'server') {
        delete tableState.pagination.paginationModel;
      }
      await dataLoader(tableState, controller.signal);
    } catch (err) {
      if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
        // Only log if not a cancellation
        console.error(err);
      }
    } finally {
      setisLoading(false);
    }
  };

  useEffect(() => {
    if (Object.keys(apiRef?.current).length > 0 && !disableSave) {
      apiRef.current.subscribeEvent('aggregationModelChange', handleEvent);
      apiRef.current.subscribeEvent('columnVisibilityModelChange', handleEvent);
      apiRef.current.subscribeEvent('columnOrderChange', handleEvent);
      apiRef.current.subscribeEvent('columnWidthChange', handleEvent);
      if (!filterFromInitialState) apiRef.current.subscribeEvent('filterModelChange', handleEvent);
      if (!filterFromInitialState) apiRef.current.subscribeEvent('paginationModelChange', handleEvent);
      apiRef.current.subscribeEvent('rowGroupingModelChange', handleEvent);
      apiRef.current.subscribeEvent('sortModelChange', handleEvent);
      apiRef.current.subscribeEvent('onDensityChange', handleEvent);
      apiRef.current.subscribeEvent('pinnedColumnsChange', handleEvent);
    }
  }, [apiRef.current, disableSave]);

  useEffect(() => {
    if (disableSave) return;
    const savedDatagridState = apiRef.current.exportState();
    if (savedDatagridState && Object.keys(apiRef?.current).length > 0) {
      if (paginationAndfilterMode === 'client' && props.rows.length > 0) {
        const isNormalPagination = [25, 50, 100].includes(savedDatagridState.pagination.paginationModel.pageSize);
        if (!isNormalPagination) {
          savedDatagridState.pagination.paginationModel.pageSize = props.rows.length > 0 ? props.rows.length : 25;
          savedDatagridState.pagination.paginationModel.page = 0;
        }
      }
      apiRef.current.restoreState(savedDatagridState);
    }
  }, [props.rows.length, disableSave]);

  useEffect(() => {
    if (Object.keys(user).length > 0 && isInitialLoad) {
      if (disableSave) {
        const options = {
          filter: datagridInitialState?.filter,
          pagination: datagridInitialState?.pagination,
          sorting: datagridInitialState?.sorting,
        };
        setQueryOptions(options);
        if (paginationAndfilterMode === 'server') {
          loader(options);
        }
        setIsInitialLoad(false);
        return;
      }

      const savedDatagridState = user.views?.dataGrid?.[datagridIdentifier];

      if (savedDatagridState) {
        const options = {
          filter: !filterFromInitialState
            ? savedDatagridState?.filter ?? datagridInitialState?.filter
            : datagridInitialState?.filter,
          pagination: !filterFromInitialState
            ? savedDatagridState?.pagination ?? datagridInitialState?.pagination
            : datagridInitialState?.pagination,
          sorting: !filterFromInitialState
            ? savedDatagridState?.sorting ?? datagridInitialState?.sorting
            : datagridInitialState?.sorting,
        };

        setInitialState(savedDatagridState);
        pendingRestoreStateRef.current = savedDatagridState;
        if (Object.keys(apiRef?.current ?? {}).length > 0) {
          apiRef.current.restoreState(savedDatagridState);
          apiRef.current.hidePreferences();
          pendingRestoreStateRef.current = null;
        }

        const currentFields = props.columns.map((col) => col.field);
        const savedFields = savedDatagridState?.columns?.orderedFields;
        const newFields = currentFields.filter((field) => !savedFields?.includes(field));
        const updatedFields = [...(savedFields?.filter((field) => currentFields.includes(field)) ?? []), ...newFields];

        // Ha van változás, mentsük el az új állapotot
        if (!isEqual(savedFields, updatedFields) && savedDatagridState.columns) {
          savedDatagridState.columns.orderedFields = updatedFields;
          saveView('dataGrid', { [datagridIdentifier]: savedDatagridState });
        }

        if (!savedDatagridState?.columns) {
          const dg = apiRef.current.exportState();
          dg.columns.orderedFields = updatedFields;
          saveView('dataGrid', { [datagridIdentifier]: dg });
        }

        columnsState.setOrderedFields(updatedFields);

        setQueryOptions(options);

        if (paginationAndfilterMode === 'server') {
          loader(options);
        }

        setIsInitialLoad(false);
      } else if (!savedDatagridState) {
        const options = {
          filter: datagridInitialState?.filter,
          pagination: datagridInitialState?.pagination,
          sorting: datagridInitialState?.sorting,
        };
        setQueryOptions(options);
        if (!savedDatagridState?.columns && Object.keys(apiRef?.current ?? {}).length > 0) {
          const dg = apiRef.current.exportState();
          //dg.columns.orderedFields = updatedFields;
          saveView('dataGrid', { [datagridIdentifier]: dg });
        }
        if (paginationAndfilterMode === 'server') {
          loader(options);
        }
        setIsInitialLoad(false);
      }
    }
  }, [
    datagridIdentifier,
    user,
    isInitialLoad,
    apiRef,
    datagridInitialState,
    paginationAndfilterMode,
    loader,
    props.columns,
    saveView,
    disableSave,
  ]);

  useEffect(() => {
    if (!isInitialLoad && refetchTable) {
      loader(queryOptions);
      setRefetchTable(false);
    }
  }, [refetchTable]);

  if (!datagridIdentifier) {
    console.error('datagridIdentifier is required but was not provided.');
    return null; // Prevent the component from rendering if the identifier is not provided
  }
  if (!paginationAndfilterMode) {
    console.error('paginationAndfilterMode is required but was not provided.');
    return null; // Prevent the component from rendering if the paginationAndFilterMode is not provided
  }

  const CustomToolbar = () => {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        {gridExport && <GridToolbarExport />}
        {customHeaderComponent}
      </GridToolbarContainer>
    );
  };

  const disableEdit = () => {
    const rowsInEditMode = Object.keys(apiRef.current.state.editRows);

    for (const rowId of rowsInEditMode) {
      apiRef.current.stopRowEditMode({ id: rowId });
    }
  };

  const onFilterModelChange = (filterModel) => {
    disableEdit();
    if (!isInitialLoad) {
      const filteredItems = filterModel.items.filter((item) => {
        if (item.operator === 'isEmpty' || item.operator === 'isNotEmpty') {
          return true;
        }
        if (item.operator === 'isAnyOf') {
          return Array.isArray(item.value) && item.value.length > 0;
        }
        return item.value !== undefined && item.value !== null;
      });
      const newModel = { ...filterModel, items: filteredItems };

      const options = { ...queryOptions, filter: { filterModel: newModel } };

      setQueryOptions(options);
      if (paginationAndfilterMode === 'server') {
        loader(options);
      }
    }
  };

  const onPaginationModelChange = (paginationModel) => {
    disableEdit();
    if (!isInitialLoad) {
      let options;
      if (paginationModel.pageSize === -1 && paginationAndfilterMode === 'server') {
        options = { ...queryOptions, pagination: {} };
      } else {
        options = { ...queryOptions, pagination: { paginationModel } };
      }
      setQueryOptions(options);
      if (paginationAndfilterMode === 'server') {
        loader(options);
      }
    }
  };

  const onSortModelChange = (sortModel) => {
    disableEdit();
    if (!isInitialLoad) {
      const options = { ...queryOptions, sorting: { sortModel } };
      setQueryOptions(options);
      if (paginationAndfilterMode === 'server') {
        loader(options);
      }
    }
  };

  const handleCellKeyDown = (params, event) => {
    props.onCellKeyDown?.(params, event);

    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;

    queueMicrotask(() => {
      const focusedCell = apiRef.current.state.focus.cell;
      if (!focusedCell?.id) return;

      const row = apiRef.current.getRow(focusedCell.id);
      if (!row) return;

      props.onRowClick?.({
        id: focusedCell.id,
        row,
      });

      props.onRowSelectionModelChange?.([focusedCell.id]);
    });
  };

  return (
    <DataGridPremium
      {...props}
      onCellKeyDown={handleCellKeyDown}
      onStateChange={(...args) => {
        if (isApplyingPendingRestoreRef.current) {
          props.onStateChange?.(...args);
          return;
        }

        if (pendingRestoreStateRef.current && Object.keys(apiRef?.current ?? {}).length > 0) {
          isApplyingPendingRestoreRef.current = true;
          apiRef.current.restoreState(pendingRestoreStateRef.current);
          apiRef.current.hidePreferences();
          pendingRestoreStateRef.current = null;
          queueMicrotask(() => {
            isApplyingPendingRestoreRef.current = false;
          });
        }
        props.onStateChange?.(...args);
      }}
      pagination={props.pagination ?? true}
      columns={columnsState.columns}
      onColumnWidthChange={columnsState.onColumnWidthChange}
      onColumnOrderChange={columnsState.onColumnOrderChange}
      apiRef={apiRef}
      //initialState={filterFromInitialState ? { datagridInitialState } : { ...datagridInitialState, ...initialState }}
      initialState={{ ...datagridInitialState, ...initialState }}
      rowHeight={45}
      sx={{ ...getDataGridStyles(theme, { height: props.height ?? height }), ...props.sx }}
      slots={{ toolbar: CustomToolbar, ...props.slots }}
      slotProps={{
        ...props.slotProps,
        columnsPanel: {
          sx: {
            '& .MuiDataGrid-panelFooter button:first-child': {
              display: 'none',
            },
          },
        },
        filterPanel: {
          logicOperators: [GridLogicOperator.And],
        },
        pagination: {
          showFirstButton: true,
          showLastButton: true,
        },
      }}
      componentsProps={{
        columnMenu: {
          hide: true, // Tiltja az oszlop panel megnyitását
        },
      }}
      localeText={{ ...huHU.components.MuiDataGrid.defaultProps.localeText, ...datagridLangHunCustomized }}
      disableSelectionOnClick
      headerFilters={props.headerFilters ?? true}
      onFilterModelChange={onFilterModelChange}
      onPaginationModelChange={onPaginationModelChange}
      onSortModelChange={onSortModelChange}
      paginationMode={paginationAndfilterMode}
      sortingMode={paginationAndfilterMode}
      filterMode={paginationAndfilterMode}
      loading={props.loading || isLoading}
      pageSizeOptions={[25, 50, 100].concat(
        gridExport ? [{ value: paginationAndfilterMode === 'client' ? props.rows.length : -1, label: 'Összes' }] : []
      )}
    />
  );
};

DataGridPremiumUI.propTypes = {
  datagridIdentifier: PropTypes.oneOf(Object.values(DataGridIdentifiers)).isRequired,
  paginationAndfilterMode: PropTypes.oneOf(['server', 'client']).isRequired,
  disableSave: PropTypes.bool,
};

export default DataGridPremiumUI;
